/**
 * JoinerNode Types - Output merging and comparison node
 *
 * Collects outputs from Looper iterations and allows
 * side-by-side comparison with AI-powered merging.
 *
 * Purpose: After Looper processes items, users need to review results
 * and combine them intelligently. Joiner provides that review and merge experience.
 */

// ============================================================================
// MERGE MODE TYPES
// ============================================================================

/**
 * Merge mode for combining outputs
 *
 * - concatenate: Simple append all outputs in order
 * - merge-last: Use only the last/final result
 * - user-select: User picks which outputs to include
 * - ai-merge: AI combines selected parts intelligently
 */
export type MergeMode =
  | 'concatenate'    // Simple append all
  | 'merge-last'     // Use only the last result
  | 'user-select'    // User picks which to include
  | 'ai-merge';      // AI combines selected parts

// ============================================================================
// INPUT TYPES
// ============================================================================

/**
 * Single input to the Joiner
 *
 * Each input represents one output from a connected node
 * (typically from Looper iterations).
 */
export interface JoinerInput {
  /** Unique identifier for this input */
  id: string;

  /** Label for the input (e.g., "Iteration 1", "Item: Apple") */
  name: string;

  /** The actual data (string, object, etc.) */
  data: unknown;

  /** ID of the node that produced this output */
  sourceNodeId: string;

  /** When this input was received (ISO timestamp) */
  timestamp: string;
}

// ============================================================================
// NODE DATA
// ============================================================================

/**
 * Data stored in a JoinerNode
 *
 * This is the primary interface for JoinerNode state. It includes:
 * - Content (title, inputs, selections)
 * - Merge configuration (mode, merged output)
 * - Execution state (status, errors)
 * - Runtime callbacks (injected by BoardCanvas)
 */
export interface JoinerNodeData {
  // ========== Content ==========

  /** Node title (displayed above the node) */
  title: string;

  /** Array of inputs received from connected nodes */
  inputs: JoinerInput[];

  /** IDs of inputs selected for merging */
  selectedIds: string[];

  /** Current merge mode */
  mergeMode: MergeMode;

  // ========== Merged Result ==========

  /** Result after merging selected inputs */
  mergedOutput?: unknown;

  /** Current merge operation status */
  mergeStatus: 'idle' | 'merging' | 'complete' | 'error';

  /** Error message if merge failed */
  mergeError?: string;

  // ========== Callbacks (injected at runtime by BoardCanvas) ==========

  /** Called when title changes */
  onTitleChange?: (title: string) => void;

  /** Called when selection changes */
  onSelectionChange?: (ids: string[]) => void;

  /** Called when merge mode changes */
  onMergeModeChange?: (mode: MergeMode) => void;

  /** Called when user triggers merge */
  onMerge?: () => void;

  // ========== Runtime Context (injected by BoardCanvas) ==========

  /** Yjs document for collaboration (not stored in Yjs) */
  ydoc?: unknown;

  /** Board ID for API calls (not stored in Yjs) */
  boardId?: string;

  /** Yjs map of all nodes (not stored in Yjs) */
  nodesMap?: unknown;

  // Index signature for React Flow compatibility
  [key: string]: unknown;
}

// ============================================================================
// REACT FLOW NODE TYPE
// ============================================================================

/**
 * React Flow node type for JoinerNode
 *
 * Use this type when working with joiner nodes in React Flow:
 * - In nodeTypes registry
 * - When creating new joiner nodes
 * - In onNodesChange handlers
 */
export type JoinerNodeType = {
  id: string;
  type: 'joiner';
  data: JoinerNodeData;
  position: { x: number; y: number };
  [key: string]: unknown;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create default JoinerNodeData with sensible defaults
 *
 * Use this when creating a new JoinerNode to ensure all required
 * fields are initialized with appropriate default values.
 */
export function createDefaultJoinerNodeData(): JoinerNodeData {
  return {
    title: 'Joiner',
    inputs: [],
    selectedIds: [],
    mergeMode: 'user-select',
    mergeStatus: 'idle',
  };
}
